/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Edit, Mail, Phone, Building2, Shield, Loader2, Save, Check, Star, Send, Award, X, UserPlus, Search, Upload, FileText, Trash2, Eye, Download, Printer, FileCheck, ScrollText, ShieldCheck, Calendar, Hash, MapPin, Globe, Users, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { DatePickerFr } from '../ui/DatePickerFr';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '../../lib/utils';
import { formatCodeNAF } from '../../utils/codeNaf';

const API = process.env.REACT_APP_BACKEND_URL;

export const EditFtpeDateModal = ({ membre, open, onClose, onSave, token }) => {
  const [dateAdhesion, setDateAdhesion] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open && membre) {
      // Pre-fill with existing date if available
      const existing = membre.date_adhesion_ftpe || membre.date_adhesion_custom || '';
      if (existing) {
        try { setDateAdhesion(new Date(existing).toISOString().split('T')[0]); }
        catch { setDateAdhesion(''); }
      } else {
        setDateAdhesion('');
      }
      setResult(null);
    }
  }, [open, membre]);

  const handleSave = async () => {
    if (!dateAdhesion) { toast.error('Selectionnez une date'); return; }
    setSaving(true);
    setResult(null);
    try {
      const res = await axios.put(
        `${API}/api/admin/membres/${membre.id}/ftpe-date-adhesion`,
        { date_adhesion: `${dateAdhesion}T00:00:00+00:00` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      toast.success('Date d\'adhesion FTPE modifiee');
      if (onSave) onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  if (!membre) return null;

  const isSigned = membre.statut_ftpe === 'adherent' || membre.statut_ftpe === 'actif' || membre.ftpe_actif;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-ftpe-date-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-amber-600" />
            Date d'adhesion FTPE
          </DialogTitle>
          <DialogDescription>
            Modifier la date d'adhesion FTPE de {membre.prenom} {membre.nom}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Status indicator */}
          <div className={cn("p-3 rounded-lg border text-sm flex items-center gap-3",
            isSigned ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
          )}>
            <div className={cn("w-2.5 h-2.5 rounded-full flex-shrink-0", isSigned ? "bg-emerald-500" : "bg-amber-500")} />
            <div>
              <p className="font-medium text-gray-800">{isSigned ? 'Adhesion signee' : 'Adhesion en attente de signature'}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {isSigned
                  ? 'La modification s\'appliquera au workflow et a l\'adhesion confirmee'
                  : 'La modification s\'appliquera au workflow avant signature'}
              </p>
            </div>
          </div>

          {/* Current date info */}
          {(membre.date_adhesion_ftpe || membre.date_adhesion_custom) && (
            <div className="p-2.5 rounded-lg bg-gray-50 border text-sm" data-testid="current-ftpe-date">
              <span className="text-gray-500">Date actuelle : </span>
              <span className="font-medium">
                {new Date(membre.date_adhesion_ftpe || membre.date_adhesion_custom).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
              </span>
            </div>
          )}

          {/* Date picker */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Nouvelle date d'adhesion FTPE</Label>
            <DatePickerFr
              value={dateAdhesion}
              onChange={setDateAdhesion}
              className="h-10 w-full"
              id="ftpe-date-input"
            />
            <p className="text-[11px] text-gray-400">La date d'expiration sera automatiquement calculee a 1 an</p>
          </div>

          {/* Result */}
          {result && (
            <div className="p-3 rounded-lg border bg-emerald-50 border-emerald-200 text-sm" data-testid="ftpe-date-result">
              <div className="flex items-center gap-2 mb-1">
                <Check className="h-4 w-4 text-emerald-600" />
                <span className="font-medium">{result.message}</span>
              </div>
              <div className="ml-6 space-y-0.5 text-xs text-gray-600">
                <p>Date adhesion : {new Date(result.date_adhesion).toLocaleDateString('fr-FR')}</p>
                <p>Date expiration : {new Date(result.date_expiration).toLocaleDateString('fr-FR')}</p>
                <p className="text-gray-400">{result.workflows_updated} workflow(s), {result.adhesions_updated} adhesion(s)</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Fermer</Button>
          <Button onClick={handleSave} disabled={saving || !dateAdhesion} className="bg-amber-600 hover:bg-amber-700" data-testid="save-ftpe-date-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Modifier la date
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ==================== REMISE SOLIDAIRE NÉGOCIÉE ====================
//
// Permet à un Admin / Super Admin de configurer une remise commerciale
// pré-négociée pour un membre. Le simulateur BATICO l'appliquera ensuite
// automatiquement à chaque simulation, sans intervention manuelle.
//

